import CategoryModal from "@app/components/category-modal";
import Checkbox from "@app/components/checkbox";
import ConfirmModal from "@app/components/confirm-modal";
import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  SystemMessage,
} from "@app/constants";
import CategoryService from "@app/services/http/category.service";
import { openPortalDialog } from "@app/services/modal.service";
import { Category, CreateCategory, Meta } from "@app/types";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";

function IconColumnTable({ data }: { data: Category }) {
  return (
    <div className="flex gap-2 items-center truncate min-w-[80px] max-w-[180px]">
      {data.icon && (
        <img className="w-9 h-9 rounded-lg" src={data.icon} alt="icon" />
      )}
    </div>
  );
}

function FreeColumnTable({ data }: { data: Category }) {
  return (
    <div className="w-[70px] flex justify-center">
      <Checkbox checked={data.isContainFree} disabled />
    </div>
  );
}

function PurchaseColumnTable({ data }: { data: Category }) {
  return (
    <div className="w-[70px] flex justify-center">
      <Checkbox checked={data.isContainPurchase} disabled />
    </div>
  );
}

function VipColumnTable({ data }: { data: Category }) {
  return (
    <div className="w-[70px] flex justify-center">
      <Checkbox checked={data.isContainVip} disabled />
    </div>
  );
}

const columnConfig: ColumnConfig[] = [
  {
    label: "ID",
    dataKey: "id",
    width: "10%",
    tdClassName: "truncate min-w-[80px] max-w-[180px]",
    hasOrder: true,
  },
  {
    label: "Tên danh mục",
    dataKey: "name",
    width: "40%",
    tdClassName: "truncate min-w-[100px] max-w-[250px]",
    hasOrder: true,
  },
  {
    label: "Tiêu đề",
    dataKey: "title",
    width: "40%",
    tdClassName: "truncate min-w-[100px] max-w-[250px]",
    hasOrder: true,
  },
  {
    label: "Mô tả",
    dataKey: "description",
    width: "40%",
    tdClassName: "truncate min-w-[180px] max-w-[280px]",
    hasOrder: true,
  },
  {
    label: "Icon",
    dataKey: "icon",
    width: "10%",
    component: IconColumnTable,
    hasOrder: false,
  },
  {
    label: "Hiển thị sách FREE",
    dataKey: "isContainFree",
    width: "10%",
    component: FreeColumnTable,
    hasOrder: false,
  },
  {
    label: "Hiển thị sách mua lẻ",
    dataKey: "isContainPurchase",
    width: "10%",
    component: PurchaseColumnTable,
    hasOrder: false,
  },
  {
    label: "Hiển thị sách VIP",
    dataKey: "isContainVip",
    width: "10%",
    component: VipColumnTable,
    hasOrder: false,
  },
  {
    label: "Order",
    dataKey: "order",
    width: "10%",
    tdClassName: "truncate min-w-[80px] max-w-[180px]",
    hasOrder: true,
  },
];

function CategoryManagement() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();

  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    CategoryService.getList(requestParams, abortController.signal).then(
      (res) => {
        setCategories(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }
      }
    );
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleCreateClick = () => {
    const categoryModalObs = openPortalDialog(CategoryModal);

    categoryModalObs.afterClosed().subscribe((data) => {
      if (data?.category) {
        const category: CreateCategory = {
          title: data.category.title,
          name: data.category.name,
          description: data.category.description,
          icon: data.category.icon,
          order: data.category.order,
          isContainFree: data.category.isContainFree,
          isContainPurchase: data.category.isContainPurchase,
          isContainVip: data.category.isContainVip,
        };

        CategoryService.create(category).then(() => {
          addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleEditClick = (category: Category) => {
    const categoryModalObs = openPortalDialog(CategoryModal, {
      isEdit: true,
      category,
    });

    categoryModalObs.afterClosed().subscribe((data) => {
      if (data?.category) {
        const createCategory: CreateCategory = {
          title: data.category.title,
          name: data.category.name,
          description: data.category.description,
          order: data.category.order,
          isContainFree: data.category.isContainFree,
          isContainPurchase: data.category.isContainPurchase,
          isContainVip: data.category.isContainVip,
        };

        if (data.category.icon) {
          createCategory.icon = data.category.icon;
        }

        CategoryService.update(category.id, createCategory).then(() => {
          addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleDeleteClick = (categoryId: number) => {
    if (categoryId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_CATEGORY_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          CategoryService.delete(categoryId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách danh mục</div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={handleCreateClick}
          >
            <div className="flex gap-4">
              <img src={Images.PlusIcon.default} alt="" />
              <div>Tạo mới</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách danh mục"
          onInputChange={onSearchChange}
        />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={categories}
            hasCheckBox
            hasAction
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default CategoryManagement;
